import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X, UserPlus } from 'lucide-react';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@aaas/shared';
import type {
  CalendarAttendeeCreateInput,
  CalendarAttendeeRole,
} from '../types';

export interface WorkspaceMember {
  account_id: string;
  account_name: string;
  account_email: string;
}

interface AttendeeRowState {
  name: string;
  email: string;
}

interface CalendarAttendeeSectionProps {
  label: string;
  role: CalendarAttendeeRole;
  attendees: CalendarAttendeeCreateInput[];
  onAdd: (name: string, email: string, role: CalendarAttendeeRole) => void;
  onAddFromAccount: (member: WorkspaceMember, role: CalendarAttendeeRole) => void;
  onRemove: (index: number) => void;
  disabled: boolean;
  members: WorkspaceMember[];
}

export function CalendarAttendeeSection({
  label,
  role,
  attendees,
  onAdd,
  onAddFromAccount,
  onRemove,
  disabled,
  members,
}: CalendarAttendeeSectionProps) {
  const { t } = useTranslation('ats');
  const [row, setRow] = useState<AttendeeRowState>({ name: '', email: '' });
  const [isManualMode, setIsManualMode] = useState(false);
  const [selectKey, setSelectKey] = useState(0);

  const handleAdd = () => {
    if (!row.name.trim() || !row.email.trim()) return;
    onAdd(row.name.trim(), row.email.trim(), role);
    setRow({ name: '', email: '' });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleSelectMember = (accountId: string) => {
    const member = members.find((m) => m.account_id === accountId);
    if (member) {
      onAddFromAccount(member, role);
    }
    setSelectKey((k) => k + 1);
  };

  const addedAccountIds = new Set(attendees.map((a) => a.account_id).filter(Boolean));
  const availableMembers = members.filter((m) => !addedAccountIds.has(m.account_id));

  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {label}
      </Label>
      {attendees.length > 0 && (
        <div className="space-y-1">
          {attendees.map((att, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between px-3 py-1.5 bg-muted rounded-md text-sm"
            >
              <span>
                {att.display_name}
                {att.email && (
                  <span className="text-muted-foreground ml-1">({att.email})</span>
                )}
              </span>
              <button
                type="button"
                onClick={() => onRemove(idx)}
                className="text-muted-foreground hover:text-destructive"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {isManualMode ? (
        <div className="space-y-1.5">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Input
                placeholder={t('calendar.attendee.namePlaceholder')}
                value={row.name}
                onChange={(e) => setRow((prev) => ({ ...prev, name: e.target.value }))}
                onKeyDown={handleKeyDown}
                disabled={disabled}
              />
            </div>
            <div className="flex-1">
              <Input
                placeholder={t('calendar.attendee.emailPlaceholder')}
                type="email"
                value={row.email}
                onChange={(e) => setRow((prev) => ({ ...prev, email: e.target.value }))}
                onKeyDown={handleKeyDown}
                disabled={disabled}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAdd}
              disabled={disabled || !row.name.trim() || !row.email.trim()}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <button
            type="button"
            onClick={() => setIsManualMode(false)}
            className="text-xs text-blue-600 hover:underline"
          >
            {t('calendar.attendee.pickFromWorkspace')}
          </button>
        </div>
      ) : (
        <div className="space-y-1.5">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Select key={selectKey} onValueChange={handleSelectMember}>
                <SelectTrigger>
                  <SelectValue placeholder={t('calendar.attendee.selectMember')} />
                </SelectTrigger>
                <SelectContent>
                  {availableMembers.map((member) => (
                    <SelectItem key={member.account_id} value={member.account_id}>
                      {member.account_name} ({member.account_email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsManualMode(true)}
            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
          >
            <UserPlus className="h-3 w-3" />
            {t('calendar.attendee.addExternal')}
          </button>
        </div>
      )}
    </div>
  );
}
